import { repositoryPos } from "../_base/_const/_constVar";
import { MessageResponse } from "../model/MessageResponse";

/**
 * Interface cho route từ VietMap API
 */
export interface VietMapRoute {
  sourceAddress: string;
  destinationAddress: string;
  distanceKm: number;
  durationMinutes: number;
  durationSeconds: number;
  distanceMeters: number;
}

/**
 * Interface cho data từ VietMap API
 */
export interface VietMapDistanceMatrixData {
  success: boolean;
  message: string;
  routes: VietMapRoute[];
}

/**
 * Interface tương thích với API cũ (Google Maps format)
 */
export interface LegacyDistanceMatrixResponse {
  rows: Array<{
    elements: Array<{
      distance: {
        text: string;
        value: number; // meters
      };
      duration: {
        text: string;
        value: number; // seconds
      };
      status: string;
    }>;
  }>;
}

/**
 * Interface cho kết quả trả về
 */
export interface DistanceMatrixResult {
  success: boolean;
  message?: string;
  data: LegacyDistanceMatrixResponse;
}

/**
 * Tính khoảng cách giữa các địa chỉ sử dụng VietMap API
 * @param sourceAddresses - Mảng địa chỉ xuất phát
 * @param destinationAddresses - Mảng địa chỉ đích
 * @returns Response tương thích với format cũ (Google Maps)
 */
export const calculateDistanceMatrix = async (
  sourceAddresses: string[],
  destinationAddresses: string[]
): Promise<DistanceMatrixResult> => {
  try {
    const response = await repositoryPos.post<
      MessageResponse<VietMapDistanceMatrixData>
    >("/api/v1/VietMap/calculate-distance-matrix", {
      sourceAddresses,
      destinationAddresses,
    });

    const vietMapData = response?.data;

    if (
      !vietMapData ||
      !vietMapData.routes ||
      vietMapData.routes.length === 0
    ) {
      return {
        success: false,
        message: "Không thể tính khoảng cách",
        data: {
          rows: [],
        },
      };
    }

    // Transform VietMap response to legacy Google Maps format
    // VietMap trả về mảng routes phẳng, cần nhóm lại theo source
    const numSources = sourceAddresses.length;
    const numDestinations = destinationAddresses.length;

    const legacyFormat: LegacyDistanceMatrixResponse = {
      rows: [],
    };

    // Nhóm routes thành rows theo source
    for (let sourceIndex = 0; sourceIndex < numSources; sourceIndex++) {
      const elements = [];

      for (let destIndex = 0; destIndex < numDestinations; destIndex++) {
        const routeIndex = sourceIndex * numDestinations + destIndex;
        const route = vietMapData.routes[routeIndex];

        if (route) {
          elements.push({
            distance: {
              text: `${route.distanceKm.toFixed(1)} km`,
              value: Math.round(route.distanceMeters), // meters
            },
            duration: {
              text:
                route.durationMinutes >= 60
                  ? `${Math.floor(route.durationMinutes / 60)} giờ ${
                      route.durationMinutes % 60
                    } phút`
                  : `${route.durationMinutes} phút`,
              value: route.durationSeconds, // seconds
            },
            status: "OK",
          });
        } else {
          // Fallback nếu không có route
          elements.push({
            distance: {
              text: "0 km",
              value: 0,
            },
            duration: {
              text: "0 phút",
              value: 0,
            },
            status: "NOT_FOUND",
          });
        }
      }

      legacyFormat.rows.push({ elements });
    }

    return {
      success: true,
      message: "Tính khoảng cách thành công",
      data: legacyFormat,
    };
  } catch (error: any) {
    console.error("Error calculating distance matrix:", error);
    return {
      success: false,
      message: error.message || "Lỗi khi tính khoảng cách",
      data: {
        rows: [],
      },
    };
  }
};

/**
 * Legacy function - Tương thích với API cũ (Google Maps)
 * @deprecated Sử dụng calculateDistanceMatrix thay thế
 */
export const calculateDistanceMatrixLegacy = async (
  origins: string[],
  destinations: string[]
): Promise<DistanceMatrixResult> => {
  return calculateDistanceMatrix(origins, destinations);
};
